import {Component, inject, Input, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {Region} from '@6grain/masofa-monolith-api-client/dictionaries';
import {ServerTranslatePipe} from '@app/utils/pipes/server-translate-pipe';
import {EraWeatherDataService} from '@6grain/masofa-monolith-api-client/weather';
import {DataTableComponent, TableColumn, TableConfig} from '@components/data-table/data-table.component';
import {PrimaryButton} from '@components/button/primary-button/primary-button';
import {SecondaryButton} from '@components/button/secondary-button/secondary-button';
import {DialogModule} from 'primeng/dialog';
import {environment} from '@root/src/environments/environment';

export interface ProblemCrop {
  cropName: string;
  areaHa: number;
  areaPercent: number;
}

export interface RegionProblemData {
  regionId: string;
  crops: ProblemCrop[];
}

@Component({
  selector: 'app-problem-areas',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ServerTranslatePipe,
    DataTableComponent,
    PrimaryButton,
    SecondaryButton,
    DialogModule
  ],
  templateUrl: './problem-areas.component.html',
  styleUrls: ['./problem-areas.component.scss']
})
export class ProblemAreasComponent implements OnInit {
  @Input() allRegionsLevel2: Region[] = [];
  @Input() totalRegions: number = 0;
  @Input() totalArea: string = '';

  protected eraWeatherDataService: EraWeatherDataService = inject(EraWeatherDataService);
  protected tr: TranslateService = inject(TranslateService);

  // Problem crops data by region
  problemCropsByRegion: Map<string, ProblemCrop[]> = new Map();

  // Modal state
  showModal: boolean = false;
  selectedRegion: Region | null = null;
  selectedRegionCrops: ProblemCrop[] = [];

  // Table configuration
  tableColumns: TableColumn[] = [
    {field: 'cropName', header: 'Name2', type: 'text'},
    {field: 'areaHa', header: 'Area (ha)', type: 'number'},
    {field: 'areaPercent', header: '% of region', type: 'number'}
  ];

  tableConfig: TableConfig = {
    paginator: false,
    selectable: false
  };

  ngOnInit(): void {
    this.loadProblemCrops();
  }

  private loadProblemCrops(): void {
    this.eraWeatherDataService.weatherEraWeatherDataGetHeatStressProblemCropsPost({
      locale: this.tr.currentLang,
      today: new Date().toISOString().split('T')[0]
    }).subscribe({
      next: (data: any) => {
        // Assuming data is a key-value object with regionId as key
        if (data && typeof data === 'object') {
          Object.keys(data).forEach(regionId => {
            const crops = data[regionId];
            if (Array.isArray(crops)) {
              this.problemCropsByRegion.set(regionId, crops.map((crop: any) => ({
                cropName: crop.cropName || crop.name || '',
                areaHa: crop.areaHa || crop.area || 0,
                areaPercent: crop.areaPercent || crop.percent || 0
              })));
            }
          });
        }
      },
      error: (err) => {
        console.error('Error fetching Heat Stress Problem Crops Data:', err);
      }
    });
  }

  getProblemCount(region: Region): number {
    const crops = this.problemCropsByRegion.get(String(region.id));
    return crops ? crops.length : 0;
  }

  onRegionClick(region: Region): void {
    const crops = this.problemCropsByRegion.get(String(region.id));
    if (crops && crops.length > 0) {
      this.selectedRegion = region;
      this.selectedRegionCrops = crops;
      this.showModal = true;
    }
  }

  closeModal(): void {
    this.showModal = false;
    this.selectedRegion = null;
    this.selectedRegionCrops = [];
  }

  exportData(): void {
    // Export logic - can be implemented later
  }

  protected readonly environment = environment;
}
